import {
  useQuery,
  UseQueryResult,
  UseQueryOptions,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import { buildQueryString } from "@mimber-client/utils"
import { GroupRoleDto, ResponseDto, QueryParamsDto } from "@mimber/schema"
import { groupRolesKeys } from "../group-roles.utils"

/**
 * Hook for fetching group roles
 * Can only be used in mimber-organization app
 *
 * @param groupUuid - Group UUID
 * @param params - Query parameters (pagination, sorting, filtering)
 * @param options - React Query options
 */
export const useGroupRoles = (
  groupUuid: string,
  params?: QueryParamsDto,
  options?: UseQueryOptions<ResponseDto<GroupRoleDto[]>, Error>
): UseQueryResult<ResponseDto<GroupRoleDto[]>, Error> => {
  guardAppUsage("mimber-organization", "useGroupRoles")

  const { server } = useServerContext()

  return useQuery({
    queryKey: groupRolesKeys.findAll(groupUuid, params),
    queryFn: async () => {
      const response = await server.get<ResponseDto<GroupRoleDto[]>>(
        buildQueryString(`groups/${groupUuid}/roles`, params)
      )
      return response.data
    },
    staleTime: 5 * 60 * 1000, // Consider roles data fresh for 5 minutes
    enabled: !!groupUuid, // Only run query if groupUuid is provided
    ...options,
  })
}
